import React from 'react';

import { bool, node, number, string } from 'prop-types';

import Box from '@mui/material/Box';

import useMediaInfo from 'hooks/useMediaInfo';

const Grid = React.forwardRef(
  (
    {
      children,
      className,
      columns,
      columnSpacing,
      component,
      container,
      desktop,
      display,
      item,
      mobile,
      rowSpacing,
      spacing,
      tablet,
      ...restProps
    },
    ref,
  ) => {
    const { isMobile, isTablet, isDesktop } = useMediaInfo();

    // this removes the need to pass in spans for each breakpoint if we have the same layout across all screen sizes
    const renderColumnSpan = () => {
      let colSpan = 'auto';
      if (isMobile && mobile) {
        colSpan = mobile;
      } else if (isTablet && (tablet || mobile)) {
        const firstAvailableValue = tablet || mobile;
        colSpan = firstAvailableValue;
      } else if (isDesktop && (desktop || tablet || mobile)) {
        const firstAvailableValue = desktop || tablet || mobile;
        colSpan = firstAvailableValue;
      }

      return `span ${colSpan}`;
    };

    // Using the Box component instead of MUI's Grid due to a number of small quirks, mainly that the xs, sm, md, and lg props on Grid items
    // don't respect custom/overridden breakpoint values and will revert back to MUI's default breakpoints.
    // This component allows us to have a better 1:1 mapping of our useMediaInfo breakpoints along with this component's mobile, tablet, and desktop props
    // and can be extended to accept updated/extra breakpoints or other props as needed. Coupled with the common Flexbox component, this Grid component
    // should solve for most of our layout needs.

    return (
      <Box
        className={className}
        columnGap={columnSpacing}
        component={component}
        display={container ? 'grid' : display}
        gap={spacing}
        gridColumn={item ? renderColumnSpan() : null}
        gridTemplateColumns={container ? `repeat(${columns}, 1fr)` : null}
        ref={ref}
        rowGap={rowSpacing}
        {...restProps}
      >
        {children}
      </Box>
    );
  },
);

export default Grid;

// each of the spacing props multiplies the number passed in by 8px
// ex. spacing={2} would come out to 16px of space between rows and columns within container
Grid.defaultProps = {
  className: undefined,
  columns: 12,
  columnSpacing: undefined,
  component: 'div',
  container: undefined,
  desktop: undefined,
  display: 'block',
  item: undefined,
  mobile: undefined,
  spacing: undefined,
  rowSpacing: undefined,
  tablet: undefined,
};

Grid.propTypes = {
  children: node.isRequired,
  className: string,
  columns: number,
  columnSpacing: number,
  component: string,
  container: bool,
  desktop: number,
  display: string,
  item: bool,
  mobile: number,
  rowSpacing: number,
  spacing: number,
  tablet: number,
};
